import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Plus, Power, Copy, Trash2, Pencil, Upload, Image as ImageIcon, FileText, Download, X, Sparkles, Archive, ArchiveRestore, TrendingUp, Eye } from 'lucide-react';
import { toast } from 'sonner';
import VipTaxSection, { VipAcceptanceHistory } from '../../components/VipTaxSection';
import { startPortalPreview } from '../../lib/portalPreview';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const blankManual = () => ({
  vip_id: '', customer_name: '', customer_email: '', customer_phone: '',
  invoice_reference: '', final_paid: 0, bonus_amount: 50,
  status: 'pending', notes: '', referral_date: new Date().toISOString().slice(0, 10),
});

const blankEdit = () => ({
  vip_id: '', name: '', email: '', phone: '', notes: '', active: true,
  promo_code: '', new_promo_code: '',
});

const AdminVips = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [vips, setVips] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', promo_code: '' });
  const [referrals, setReferrals] = useState([]);
  const [manualOpen, setManualOpen] = useState(false);
  const [manual, setManual] = useState(blankManual());
  const [savingManual, setSavingManual] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  // Edit dialog state
  const [editOpen, setEditOpen] = useState(false);
  const [edit, setEdit] = useState(blankEdit());
  const [savingEdit, setSavingEdit] = useState(false);
  const [savingCode, setSavingCode] = useState(false);
  const [cardBusy, setCardBusy] = useState(false);
  const [performance, setPerformance] = useState(null);

  useEffect(() => { refresh(); }, [showArchived]);

  const refresh = async () => {
    try {
      const vipsUrl = showArchived ? `${API}/admin/vips?include_archived=true` : `${API}/admin/vips`;
      const [a, b] = await Promise.all([
        axios.get(vipsUrl, { withCredentials: true }),
        axios.get(`${API}/admin/referrals`, { withCredentials: true }),
      ]);
      setVips(a.data);
      setReferrals(b.data);
    } catch (err) { console.error(err); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(`${API}/admin/vips`, form, { withCredentials: true });
      toast.success(`VIP created. Code: ${data.promo_code}`);
      setForm({ name: '', email: '', password: '', promo_code: '' });
      setShowForm(false);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleToggle = async (id, active) => {
    try {
      const url = active ? `${API}/admin/vips/${id}/deactivate` : `${API}/admin/vips/${id}/activate`;
      await axios.put(url, {}, { withCredentials: true });
      toast.success(active ? 'Deactivated' : 'Activated');
      refresh();
    } catch { toast.error('Failed'); }
  };

  const handlePayout = async (bonusId, status) => {
    try {
      await axios.put(`${API}/admin/referrals/${bonusId}`, { status }, { withCredentials: true });
      toast.success(`Marked ${status}`);
      refresh();
    } catch { toast.error('Failed'); }
  };

  const copyCode = (code) => { navigator.clipboard.writeText(code); toast.success('Copied'); };

  const openEdit = (v) => {
    setEdit({
      vip_id: v.id,
      name: v.name || '',
      email: v.email || '',
      phone: v.phone || '',
      notes: v.notes || '',
      active: v.active !== false,
      promo_code: v.promo_code || '',
      new_promo_code: v.promo_code || '',
      card: v.card || null,
      archived: !!v.archived,
    });
    setEditOpen(true);
    setPerformance(null);
    // Async-fetch performance card
    axios.get(`${API}/admin/vips/${v.id}/performance`, { withCredentials: true })
      .then(({ data }) => setPerformance(data))
      .catch(() => setPerformance(null));
  };

  const archiveVip = async (e, v) => {
    e.stopPropagation();
    if (!window.confirm(`Archive VIP ${v.name}? Their referral code will be disabled. History stays intact.`)) return;
    try {
      await axios.patch(`${API}/admin/vip/${v.id}/archive`, {}, { withCredentials: true });
      toast.success('Archived');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const unarchiveVip = async (e, v) => {
    e.stopPropagation();
    try {
      await axios.patch(`${API}/admin/vip/${v.id}/unarchive`, {}, { withCredentials: true });
      toast.success('Restored');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const hardDeleteVip = async (e, v) => {
    e.stopPropagation();
    if (!window.confirm(`HARD DELETE VIP ${v.name}? Permanent. Blocked if they have any referrals or invoices using their code.`)) return;
    try {
      await axios.delete(`${API}/admin/vip/${v.id}/hard?confirm=DELETE`, { withCredentials: true });
      toast.success('Deleted permanently');
      refresh();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d && typeof d === 'object' && d.code === 'HAS_LINKED_RECORDS') {
        const linked = Object.entries(d.linked || {}).map(([k, v2]) => `${k}:${v2}`).join(', ');
        toast.error(`Cannot delete — linked: ${linked}. Archive instead.`);
      } else {
        toast.error(typeof d === 'string' ? d : 'Delete failed');
      }
    }
  };

  const saveEdit = async () => {
    if (!edit.name.trim()) { toast.error('Name is required'); return; }
    setSavingEdit(true);
    try {
      await axios.put(`${API}/admin/vips/${edit.vip_id}`, {
        name: edit.name.trim(),
        email: edit.email.trim(),
        phone: edit.phone.trim(),
        notes: edit.notes,
        active: !!edit.active,
      }, { withCredentials: true });
      toast.success('VIP profile updated');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save VIP');
    }
    setSavingEdit(false);
  };

  const changeCode = async () => {
    const newCode = (edit.new_promo_code || '').trim().toUpperCase();
    if (!newCode) { toast.error('Enter a new code'); return; }
    if (newCode === edit.promo_code) { toast.info('Code is unchanged'); return; }
    setSavingCode(true);
    try {
      const { data } = await axios.put(`${API}/admin/vips/${edit.vip_id}/promo-code`,
        { new_code: newCode },
        { withCredentials: true });
      toast.success(`Code updated to ${data.promo_code}`);
      setEdit((s) => ({ ...s, promo_code: data.promo_code, new_promo_code: data.promo_code }));
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change code');
    }
    setSavingCode(false);
  };

  const uploadCard = async (file) => {
    if (!file) return;
    const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'application/pdf'];
    if (!allowed.includes(file.type)) {
      toast.error('Use PNG, JPG, WEBP, or PDF only');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File must be under 10MB');
      return;
    }
    setCardBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/admin/vips/${edit.vip_id}/card`, fd, {
        withCredentials: true,
      });
      toast.success('Card uploaded');
      setEdit((s) => ({ ...s, card: data }));
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
    setCardBusy(false);
  };

  const deleteCard = async () => {
    if (!window.confirm('Remove this VIP card?')) return;
    setCardBusy(true);
    try {
      await axios.delete(`${API}/admin/vips/${edit.vip_id}/card`, { withCredentials: true });
      toast.success('Card removed');
      setEdit((s) => ({ ...s, card: null }));
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
    setCardBusy(false);
  };

  const generateCard = async () => {
    if (!edit.promo_code) {
      toast.error('Set a referral code before generating the card');
      return;
    }
    setCardBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/vips/${edit.vip_id}/card/generate`, {}, { withCredentials: true });
      toast.success('Card generated from template');
      setEdit((s) => ({ ...s, card: data }));
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generate failed');
    }
    setCardBusy(false);
  };

  const cardPreviewUrl = edit.vip_id && edit.card
    ? `${API}/vips/${edit.vip_id}/card?t=${edit.card.uploaded_at || ''}`
    : '';

  const saveManual = async (e) => {
    e.preventDefault();
    if (!manual.vip_id) { toast.error('Select a VIP'); return; }
    if (!manual.customer_name.trim()) { toast.error('Customer name is required'); return; }
    setSavingManual(true);
    try {
      await axios.post(`${API}/admin/referrals/manual`, {
        ...manual,
        final_paid: parseFloat(manual.final_paid || 0),
        bonus_amount: parseFloat(manual.bonus_amount || 0),
      }, { withCredentials: true });
      toast.success('Manual referral added');
      setManual(blankManual());
      setManualOpen(false);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
    setSavingManual(false);
  };

  const deleteManual = async (r) => {
    if (!window.confirm(`Delete the manual referral for ${r.customer_name}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/referrals/${r.id}`, { withCredentials: true });
      toast.success('Deleted');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div data-testid="admin-vips-page" className="space-y-8">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-2">VIP Referrals</h2>
          <p className="text-[#A3A3A3]">Manage VIP members, promo codes, savings cards, and payouts</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-sm text-white cursor-pointer">
            <input
              data-testid="vips-show-archived"
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="accent-[#D4AF37]"
            />
            Show archived
          </label>
          <Button data-testid="admin-add-vip-btn" onClick={() => setShowForm(!showForm)} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">
            <Plus className="w-4 h-4 mr-2" /> Add VIP
          </Button>
        </div>
      </div>

      {showForm && (
        <div className="bg-[#141414] border border-[#262626] rounded-md p-6">
          <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label className="text-white">Name</Label>
              <Input data-testid="vip-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required className="bg-[#0A0A0A] border-[#262626] text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-white">Email</Label>
              <Input data-testid="vip-email" type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} required className="bg-[#0A0A0A] border-[#262626] text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-white">Password</Label>
              <Input data-testid="vip-password" type="password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} required className="bg-[#0A0A0A] border-[#262626] text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-white">Promo Code (optional)</Label>
              <Input data-testid="vip-promo" value={form.promo_code} onChange={(e) => setForm({...form, promo_code: e.target.value.toUpperCase()})} placeholder="Auto-generated if blank" className="bg-[#0A0A0A] border-[#262626] text-white" />
            </div>
            <div className="md:col-span-4">
              <Button data-testid="vip-submit" type="submit" className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">Create VIP</Button>
            </div>
          </form>
        </div>
      )}

      {/* VIPs Table */}
      <div>
        <h3 className="font-['Outfit'] text-xl font-bold text-white mb-4">VIP Members</h3>
        <div className="bg-[#141414] border border-[#262626] rounded-md overflow-x-auto">
          <table className="w-full min-w-[800px]">
            <thead className="bg-[#0A0A0A] border-b border-[#262626]">
              <tr>
                <th className="text-left px-4 py-3 text-[#A3A3A3] text-sm">Name</th>
                <th className="text-left px-4 py-3 text-[#A3A3A3] text-sm">Email</th>
                <th className="text-left px-4 py-3 text-[#A3A3A3] text-sm">Promo</th>
                <th className="text-center px-4 py-3 text-[#A3A3A3] text-sm">Card</th>
                <th className="text-right px-4 py-3 text-[#A3A3A3] text-sm">Earned</th>
                <th className="text-right px-4 py-3 text-[#A3A3A3] text-sm">Paid</th>
                <th className="text-right px-4 py-3 text-[#A3A3A3] text-sm">Actions</th>
              </tr>
            </thead>
            <tbody>
              {vips.length === 0 ? (
                <tr><td colSpan={7} className="text-center px-6 py-8 text-[#A3A3A3]">No VIPs yet</td></tr>
              ) : vips.map((v) => {
                const summary = v.bonus_summary || {};
                const earned = (summary.approved?.total || 0) + (summary.paid?.total || 0);
                const paid = summary.paid?.total || 0;
                return (
                  <tr key={v.id} data-testid={`vip-row-${v.id}`} className="border-b border-[#262626] last:border-0 hover:bg-[#0A0A0A]/40">
                    <td className="px-4 py-3 text-white">
                      <div>{v.name}</div>
                      {v.active === false && !v.archived && <Badge className="mt-1 bg-red-900/30 text-red-400 border-red-700">INACTIVE</Badge>}
                      {v.archived && <Badge className="mt-1 bg-amber-900/30 text-amber-400 border-amber-700">ARCHIVED</Badge>}
                    </td>
                    <td className="px-4 py-3 text-[#A3A3A3] text-sm">{v.email}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => copyCode(v.promo_code || '')} disabled={!v.promo_code} className="text-[#D4AF37] font-mono font-bold hover:underline flex items-center gap-1 disabled:opacity-40">
                        {v.promo_code || '—'} {v.promo_code && <Copy className="w-3 h-3" />}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {v.card ? (
                        <span data-testid={`vip-card-badge-${v.id}`} className="inline-flex items-center gap-1 text-green-400 text-xs">
                          {(v.card.content_type || '').startsWith('image/') ? <ImageIcon className="w-3.5 h-3.5" /> : <FileText className="w-3.5 h-3.5" />}
                          On file
                        </span>
                      ) : (
                        <span className="text-[#525252] text-xs">None</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-[#D4AF37] font-bold">${earned.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right text-green-400">${paid.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-2 justify-end">
                        <Button
                          data-testid={`vip-view-side-${v.id}`}
                          onClick={() => startPortalPreview({ id: v.id, name: v.name, email: v.email, role: 'vip' })}
                          size="sm"
                          variant="outline"
                          className="border-cyan-700/40 text-cyan-300 hover:bg-cyan-900/20"
                          title="View Their Side (read-only preview)"
                        >
                          <Eye className="w-4 h-4 mr-1" /> View
                        </Button>
                        <Button data-testid={`vip-edit-${v.id}`} onClick={() => openEdit(v)} size="sm" variant="outline" className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10">
                          <Pencil className="w-4 h-4 mr-1" /> Edit
                        </Button>
                        <Button data-testid={`vip-toggle-${v.id}`} onClick={() => handleToggle(v.id, v.active !== false)} size="sm" variant="outline" className={`border-[#262626] ${v.active !== false ? 'text-red-400' : 'text-green-400'} hover:bg-[#262626]`}>
                          <Power className="w-4 h-4" />
                        </Button>
                        {v.archived ? (
                          <Button data-testid={`vip-unarchive-${v.id}`} onClick={(e) => unarchiveVip(e, v)} size="sm" variant="outline" className="border-green-700/40 text-green-400 hover:bg-green-700/10" title="Restore">
                            <ArchiveRestore className="w-4 h-4" />
                          </Button>
                        ) : (
                          <Button data-testid={`vip-archive-${v.id}`} onClick={(e) => archiveVip(e, v)} size="sm" variant="outline" className="border-[#262626] text-amber-400 hover:bg-amber-900/20" title="Archive">
                            <Archive className="w-4 h-4" />
                          </Button>
                        )}
                        {isSuperAdmin && (
                          <Button data-testid={`vip-delete-${v.id}`} onClick={(e) => hardDeleteVip(e, v)} size="sm" variant="outline" className="border-red-700/40 text-red-400 hover:bg-red-900/20" title="Hard delete (super_admin)">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit VIP Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="edit-vip-dialog" className="bg-[#141414] border-[#262626] text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">Edit VIP — {edit.name || '—'}</DialogTitle>
            <DialogDescription className="text-[#A3A3A3] text-xs">Update profile, change the referral code live, or assign a savings card.</DialogDescription>
          </DialogHeader>

          {/* Performance — admin-only revenue summary */}
          {performance && (
            <section data-testid="vip-performance-card" className="bg-[#0A0A0A] border border-[#D4AF37]/30 rounded-md p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-[#D4AF37] text-xs font-bold tracking-[0.2em] uppercase flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" /> Revenue Performance (Admin only)
                </h4>
                <span className="text-[#525252] text-[10px]">VIPs do not see this</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <PerfTile data-testid="perf-gross-revenue" label="Gross revenue brought in" value={`$${performance.gross_referral_revenue.toLocaleString()}`} accent="text-[#D4AF37]" />
                <PerfTile data-testid="perf-bonuses-earned" label="Bonuses earned" value={`$${performance.bonuses_earned.toLocaleString()}`} accent="text-white" />
                <PerfTile data-testid="perf-bonuses-paid" label="Already paid" value={`$${performance.bonuses_paid.toLocaleString()}`} accent="text-green-400" />
                <PerfTile data-testid="perf-owed" label="Still owed" value={`$${performance.remaining_owed.toLocaleString()}`} accent={performance.remaining_owed > 0 ? 'text-amber-400' : 'text-[#525252]'} />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <PerfTile data-testid="perf-referral-count" label="Total referrals" value={performance.referral_count} accent="text-white" />
                <PerfTile data-testid="perf-avg-referral" label="Avg referral value" value={`$${(performance.average_referral_value || 0).toLocaleString()}`} accent="text-white" />
                <PerfTile data-testid="perf-largest-referral" label="Largest referral" value={`$${(performance.largest_referral || 0).toLocaleString()}`} accent="text-[#D4AF37]" />
                <PerfTile data-testid="perf-last-referral" label="Last referral" value={performance.last_referral_date ? new Date(performance.last_referral_date).toLocaleDateString() : '—'} accent="text-white" />
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-[#A3A3A3] pt-1 border-t border-[#262626]">
                <span><span className="text-white font-semibold">{performance.paid_invoice_count}</span> paid invoices</span>
                <span><span className="text-white font-semibold">{performance.manual_referral_count}</span> manual entries</span>
                {performance.bonuses_pending > 0 && (
                  <span className="text-amber-400">${performance.bonuses_pending.toLocaleString()} pending approval</span>
                )}
              </div>
            </section>
          )}

          {/* Tax / 1099 / W-9 — admin-only */}
          <VipTaxSection vipId={edit.vip_id} />

          {/* Terms acceptance history */}
          <VipAcceptanceHistory vipId={edit.vip_id} />

          {/* Profile */}
          <section className="space-y-3">
            <h4 className="text-[#D4AF37] text-xs font-bold tracking-[0.2em] uppercase">Profile</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-white text-xs">Name *</Label>
                <Input data-testid="edit-vip-name" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white h-11" />
              </div>
              <div>
                <Label className="text-white text-xs">Email *</Label>
                <Input data-testid="edit-vip-email" type="email" value={edit.email} onChange={(e) => setEdit({ ...edit, email: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white h-11" />
              </div>
              <div>
                <Label className="text-white text-xs">Phone</Label>
                <Input data-testid="edit-vip-phone" type="tel" value={edit.phone} onChange={(e) => setEdit({ ...edit, phone: e.target.value })} placeholder="(555) 555-0100" className="bg-[#0A0A0A] border-[#262626] text-white h-11" />
              </div>
              <div>
                <Label className="text-white text-xs">Status</Label>
                <Select value={edit.active ? 'active' : 'inactive'} onValueChange={(v) => setEdit({ ...edit, active: v === 'active' })}>
                  <SelectTrigger data-testid="edit-vip-status" className="bg-[#0A0A0A] border-[#262626] text-white h-11"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-[#262626]">
                    <SelectItem value="active" className="text-green-400">Active</SelectItem>
                    <SelectItem value="inactive" className="text-red-400">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-white text-xs">Internal Notes</Label>
              <Textarea data-testid="edit-vip-notes" rows={3} value={edit.notes} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} placeholder="Private admin notes about this VIP" className="bg-[#0A0A0A] border-[#262626] text-white text-base" />
            </div>
            <Button data-testid="edit-vip-save" onClick={saveEdit} disabled={savingEdit} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">
              {savingEdit ? 'Saving…' : 'Save Profile'}
            </Button>
          </section>

          <div className="my-4 border-t border-[#262626]" />

          {/* Live referral code editor */}
          <section className="space-y-3">
            <h4 className="text-[#D4AF37] text-xs font-bold tracking-[0.2em] uppercase">Referral Code (Live)</h4>
            <p className="text-[#A3A3A3] text-xs">Changing the code is logged. Past referrals stay attached to this VIP — only the active code changes.</p>
            <div className="flex flex-col sm:flex-row gap-2 items-stretch">
              <Input
                data-testid="edit-vip-code"
                value={edit.new_promo_code}
                onChange={(e) => setEdit({ ...edit, new_promo_code: e.target.value.toUpperCase() })}
                placeholder="ASHLEY10"
                className="bg-[#0A0A0A] border-[#262626] text-white font-mono text-lg h-12 tracking-wider"
              />
              <Button data-testid="edit-vip-code-save" onClick={changeCode} disabled={savingCode || !edit.new_promo_code || edit.new_promo_code === edit.promo_code} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] h-12 px-6">
                {savingCode ? 'Updating…' : 'Update Code'}
              </Button>
            </div>
            <p className="text-[#525252] text-[10px]">3–24 characters: A–Z, 0–9, dash, or underscore.</p>
          </section>

          <div className="my-4 border-t border-[#262626]" />

          {/* Savings card */}
          <section className="space-y-3">
            <h4 className="text-[#D4AF37] text-xs font-bold tracking-[0.2em] uppercase">VIP Savings Card</h4>
            {edit.card ? (
              <div className="bg-[#0A0A0A] border border-[#262626] rounded-md p-3 space-y-3">
                {(edit.card.content_type || '').startsWith('image/') ? (
                  <img data-testid="vip-card-preview" src={cardPreviewUrl} alt="VIP card" className="w-full max-h-72 object-contain rounded-md border border-[#262626] bg-black" />
                ) : (
                  <div className="bg-[#141414] border border-[#262626] rounded-md p-4 flex items-center gap-3">
                    <FileText className="w-8 h-8 text-[#D4AF37]" />
                    <div className="min-w-0">
                      <p className="text-white text-sm truncate">{edit.card.filename}</p>
                      <p className="text-[#525252] text-xs">{Math.round((edit.card.size || 0) / 1024)} KB · PDF</p>
                    </div>
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  <a data-testid="vip-card-download" href={cardPreviewUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-[#262626] text-[#A3A3A3] hover:text-white text-xs">
                    <Download className="w-3.5 h-3.5" /> View / Download
                  </a>
                  <Button data-testid="vip-card-generate" onClick={generateCard} disabled={cardBusy} variant="outline" size="sm" className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 h-8">
                    <Sparkles className="w-3.5 h-3.5 mr-1" /> {edit.card?.generated ? 'Re-generate' : 'Generate'} from template
                  </Button>
                  <label data-testid="vip-card-replace-label" className={`inline-flex items-center gap-1 px-3 py-2 rounded-md border border-[#262626] text-[#A3A3A3] hover:text-white text-xs cursor-pointer ${cardBusy ? 'opacity-50 pointer-events-none' : ''}`}>
                    <Upload className="w-3.5 h-3.5" /> Replace with upload
                    <input data-testid="vip-card-replace-input" type="file" accept="image/png,image/jpeg,image/jpg,image/webp,application/pdf" className="hidden" onChange={(e) => uploadCard(e.target.files?.[0])} />
                  </label>
                  <Button data-testid="vip-card-delete" onClick={deleteCard} disabled={cardBusy} variant="outline" size="sm" className="border-red-700 text-red-400 hover:bg-red-900/20 h-8">
                    <X className="w-3.5 h-3.5 mr-1" /> Remove
                  </Button>
                </div>
                {edit.card?.generated ? (
                  <p className="text-[#525252] text-[10px]">Auto-generated from the NexFlow template — name and referral code are personalized.</p>
                ) : (
                  <p className="text-[10px] text-emerald-400">Custom uploaded card on file — VIP portal shows this instead of the auto-generated card.</p>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <Button
                  data-testid="vip-card-generate-empty"
                  onClick={generateCard}
                  disabled={cardBusy || !edit.promo_code}
                  className="w-full h-12 bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] disabled:opacity-40"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  {cardBusy ? 'Generating…' : `Generate card for ${edit.name || 'this VIP'}`}
                </Button>
                <p className="text-[#525252] text-xs text-center">
                  Auto-fills <span className="text-white">{edit.name || '—'}</span>{' '}
                  with referral code <span className="text-[#D4AF37] font-mono">{edit.promo_code || '—'}</span> from the profile above.
                </p>
                {!edit.promo_code && (
                  <p className="text-amber-400 text-xs text-center">Set a referral code first to enable auto-generate.</p>
                )}
                <div className="flex items-center gap-3 text-[#525252] text-[10px] uppercase tracking-wider">
                  <span className="flex-1 h-px bg-[#262626]" />
                  <span>or</span>
                  <span className="flex-1 h-px bg-[#262626]" />
                </div>
                <label data-testid="vip-card-upload-label" className={`flex flex-col items-center justify-center border-2 border-dashed border-[#262626] hover:border-[#D4AF37]/60 rounded-md p-6 cursor-pointer transition-colors ${cardBusy ? 'opacity-50 pointer-events-none' : ''}`}>
                  <Upload className="w-6 h-6 text-[#A3A3A3] mb-2" />
                  <p className="text-white text-sm">Upload your own card</p>
                  <p className="text-[#525252] text-xs mt-1">PNG, JPG, WEBP, or PDF — up to 10 MB</p>
                  <input data-testid="vip-card-upload-input" type="file" accept="image/png,image/jpeg,image/jpg,image/webp,application/pdf" className="hidden" onChange={(e) => uploadCard(e.target.files?.[0])} />
                </label>
              </div>
            )}
          </section>
        </DialogContent>
      </Dialog>

      {/* Referral Bonuses */}
      <div>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h3 className="font-['Outfit'] text-xl font-bold text-white">Referral Bonus Payouts</h3>
          <Dialog open={manualOpen} onOpenChange={(o) => { if (!o) setManual(blankManual()); setManualOpen(o); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-manual-referral" className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"><Plus className="w-4 h-4 mr-2" /> Manual Referral</Button>
            </DialogTrigger>
            <DialogContent className="bg-[#141414] border-[#262626] text-white max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Add Manual Referral / Payout</DialogTitle><DialogDescription className="text-[#A3A3A3] text-xs">Log a referral that didn't come through the normal invoice flow.</DialogDescription></DialogHeader>
              <form onSubmit={saveManual} className="space-y-3">
                <div>
                  <Label className="text-white">VIP / Referral Partner *</Label>
                  <Select value={manual.vip_id} onValueChange={(v) => setManual({ ...manual, vip_id: v })}>
                    <SelectTrigger data-testid="manual-vip" className="bg-[#0A0A0A] border-[#262626] text-white h-11"><SelectValue placeholder="Pick a VIP" /></SelectTrigger>
                    <SelectContent className="bg-[#141414] border-[#262626] max-h-60">
                      {vips.map((v) => (
                        <SelectItem key={v.id} value={v.id} className="text-white">{v.name} <span className="text-[#525252] font-mono text-xs">{v.promo_code || ''}</span></SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-white">Customer Name *</Label>
                  <Input data-testid="manual-customer-name" required value={manual.customer_name} onChange={(e) => setManual({ ...manual, customer_name: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white h-11 text-base" />
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-white">Customer Phone</Label>
                    <Input data-testid="manual-customer-phone" type="tel" value={manual.customer_phone} onChange={(e) => setManual({ ...manual, customer_phone: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white h-11 text-base" />
                  </div>
                  <div>
                    <Label className="text-white">Customer Email</Label>
                    <Input data-testid="manual-customer-email" type="email" value={manual.customer_email} onChange={(e) => setManual({ ...manual, customer_email: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white h-11 text-base" />
                  </div>
                </div>
                <div>
                  <Label className="text-white">Invoice / Job Reference</Label>
                  <Input data-testid="manual-invoice-ref" value={manual.invoice_reference} onChange={(e) => setManual({ ...manual, invoice_reference: e.target.value })} placeholder="INV-001 / Job #1234 / Check #4421" className="bg-[#0A0A0A] border-[#262626] text-white h-11 text-base" />
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-white">Final Paid ($) *</Label>
                    <Input data-testid="manual-final-paid" type="number" inputMode="decimal" min="0" step="0.01" required value={manual.final_paid} onChange={(e) => setManual({ ...manual, final_paid: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white h-11 text-base text-right" />
                  </div>
                  <div>
                    <Label className="text-white">Bonus ($) *</Label>
                    <Input data-testid="manual-bonus" type="number" inputMode="decimal" min="0" step="0.01" required value={manual.bonus_amount} onChange={(e) => setManual({ ...manual, bonus_amount: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white h-11 text-base text-right" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-white">Status</Label>
                    <Select value={manual.status} onValueChange={(v) => setManual({ ...manual, status: v })}>
                      <SelectTrigger data-testid="manual-status" className="bg-[#0A0A0A] border-[#262626] text-white h-11"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#141414] border-[#262626]">
                        <SelectItem value="pending" className="text-white">Pending</SelectItem>
                        <SelectItem value="approved" className="text-white">Approved</SelectItem>
                        <SelectItem value="paid" className="text-white">Paid</SelectItem>
                        <SelectItem value="rejected" className="text-white">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-white">Date</Label>
                    <Input data-testid="manual-date" type="date" value={manual.referral_date} onChange={(e) => setManual({ ...manual, referral_date: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white h-11 text-base" />
                  </div>
                </div>
                <div>
                  <Label className="text-white">Notes</Label>
                  <Textarea data-testid="manual-notes" rows={2} value={manual.notes} onChange={(e) => setManual({ ...manual, notes: e.target.value })} placeholder="Cash job at the Smith property, etc." className="bg-[#0A0A0A] border-[#262626] text-white text-base" />
                </div>
                <Button data-testid="manual-submit" type="submit" disabled={savingManual} className="w-full h-11 bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">{savingManual ? 'Saving…' : 'Add Manual Referral'}</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
        <div className="bg-[#141414] border border-[#262626] rounded-md overflow-x-auto">
          <table className="w-full min-w-[720px]">
            <thead className="bg-[#0A0A0A] border-b border-[#262626]">
              <tr>
                <th className="text-left px-6 py-3 text-[#A3A3A3] text-sm">VIP</th>
                <th className="text-left px-6 py-3 text-[#A3A3A3] text-sm">Customer</th>
                <th className="text-left px-6 py-3 text-[#A3A3A3] text-sm">Invoice / Ref</th>
                <th className="text-right px-6 py-3 text-[#A3A3A3] text-sm">Paid Amt</th>
                <th className="text-right px-6 py-3 text-[#A3A3A3] text-sm">Bonus</th>
                <th className="text-center px-6 py-3 text-[#A3A3A3] text-sm">Status</th>
                <th className="text-right px-6 py-3 text-[#A3A3A3] text-sm">Actions</th>
              </tr>
            </thead>
            <tbody>
              {referrals.length === 0 ? (
                <tr><td colSpan={7} className="text-center px-6 py-8 text-[#A3A3A3]">No referral bonuses yet</td></tr>
              ) : referrals.map((r) => (
                <tr key={r.id} className="border-b border-[#262626] last:border-0 hover:bg-[#0A0A0A]">
                  <td className="px-6 py-3 text-white">{r.vip_name} {r.manual && <Badge className="ml-2 bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/40">manual</Badge>}</td>
                  <td className="px-6 py-3 text-[#A3A3A3]">{r.customer_name}{r.customer_phone ? ` · ${r.customer_phone}` : ''}</td>
                  <td className="px-6 py-3 text-[#A3A3A3] font-mono text-sm">{r.invoice_number || r.invoice_reference || '—'}</td>
                  <td className="px-6 py-3 text-right text-white">${(r.final_paid || 0).toFixed(2)}</td>
                  <td className="px-6 py-3 text-right text-[#D4AF37] font-bold">${(r.bonus_amount || 0).toFixed(2)}</td>
                  <td className="px-6 py-3 text-center text-sm uppercase">
                    <Select value={r.status || 'pending'} onValueChange={(v) => handlePayout(r.id, v)}>
                      <SelectTrigger data-testid={`status-${r.id}`} className="bg-[#0A0A0A] border-[#262626] text-white h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#141414] border-[#262626]">
                        <SelectItem value="pending" className="text-white">Pending</SelectItem>
                        <SelectItem value="approved" className="text-white">Approved</SelectItem>
                        <SelectItem value="paid" className="text-white">Paid</SelectItem>
                        <SelectItem value="rejected" className="text-white">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-6 py-3 text-right">
                    <div className="inline-flex gap-1">
                      {r.status === 'approved' && (
                        <Button data-testid={`payout-${r.id}`} onClick={() => handlePayout(r.id, 'paid')} size="sm" className="bg-green-600 hover:bg-green-700 text-white">Mark Paid</Button>
                      )}
                      {r.manual && (
                        <Button data-testid={`del-manual-${r.id}`} onClick={() => deleteManual(r)} size="sm" variant="outline" className="border-red-700 text-red-400 hover:bg-red-900/30"><Trash2 className="w-3.5 h-3.5" /></Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const PerfTile = ({ label, value, accent, ...rest }) => (
  <div className="bg-[#141414] border border-[#262626] rounded-md p-2.5" {...rest}>
    <p className="text-[#A3A3A3] text-[10px] uppercase tracking-wider leading-tight">{label}</p>
    <p className={`text-base sm:text-lg font-bold mt-1 ${accent || 'text-white'}`}>{value}</p>
  </div>
);

export default AdminVips;
